import { isNil } from 'lodash-es';
import { BRAND_EAN_SPECIFICATIONS } from '~/connect/schemas';
import {
  IBrandTemplateForm,
  IBrandTemplatePayload,
  IBrandTemplateWithBrands,
  ICriteria,
  IBrandTemplate,
  ICriteriaUsageVariable,
} from '~/connect/types';
import { IBrand, ISegment, TranslationKeys } from '~/common/types';
import { SegmentHelpers } from '~/connect/helpers';

export type BrandTemplateTabValue = 'ALL' | ISegment['name'];

type GroupedBrands = Record<string, Record<string, IBrand[]>>;

export const TABS: Array<{
  value: BrandTemplateTabValue;
  label: keyof TranslationKeys;
}> = [
  {
    value: 'ALL',
    label: 'app.brand_template.form.tabs.all.title',
  },
  {
    value: 'PREMIUM',
    label: 'app.brand_template.form.tabs.premium.title',
  },
  {
    value: 'QUALITY',
    label: 'app.brand_template.form.tabs.quality.title',
  },
  {
    value: 'BUDGET',
    label: 'app.brand_template.form.tabs.budget.title',
  },
];

export const BRAND_EAN_SPECIFICATIONS_LOOKUP: Record<
  IBrandTemplate['brandEanSpecification'],
  keyof TranslationKeys
> = {
  [BRAND_EAN_SPECIFICATIONS.NO_SPECIFICATION]:
    'app.brand_template.form.brand_ean_configuration.no_specification',
  [BRAND_EAN_SPECIFICATIONS.WYZ_REFERENTIAL]:
    'app.brand_template.form.brand_ean_configuration.wyz_ref',
  [BRAND_EAN_SPECIFICATIONS.CONTRACT_NUMBER]:
    'app.brand_template.form.brand_ean_configuration.contract_number',
};

export function groupByLetterByTab(
  brands: IBrand[],
  segments: ISegment[],
): GroupedBrands {
  const groups: GroupedBrands = {};
  TABS.forEach((tab) => (groups[tab.value] = {}));
  const segmentNameByPublicKey =
    SegmentHelpers.getSegmentNameByPublicKeyLookup(segments);
  brands.forEach((brand) => {
    const letter = brand.name[0].toUpperCase();
    const segment = segmentNameByPublicKey[brand.segment.publicKey];
    if (isNil(segment)) {
      return;
    }
    if (!groups[segment][letter]) {
      groups[segment][letter] = [];
    }
    if (!groups['ALL'][letter]) {
      groups['ALL'][letter] = [];
    }
    groups[segment][letter].push(brand);
    groups['ALL'][letter].push(brand);
  });
  return groups;
}

export function fromFormToPayload(
  data: IBrandTemplateForm,
  criteria: ICriteria[],
): IBrandTemplatePayload {
  return {
    name: data.name,
    brandEanSpecification: data.brandEanSpecification,
    brands: data.brands.map((brand) => ({
      externalId: { id: brand.publicKey },
      brandEanSpecification: brand.brandEanSpecification,
      desadv: Boolean(brand.desadv),
      recommended: Boolean(brand.recommended),
    })),
    criteria: data.criteria.map(({ criterion, values }) => ({
      criterion,
      values:
        criteria
          .find((criteria) => criteria.id === criterion.id)
          ?.values.filter((value) =>
            values.some((selectedValueId) => selectedValueId === value.id),
          ) ?? [],
    })),
  };
}

export function toFormData(
  brandTemplate: IBrandTemplateWithBrands,
): IBrandTemplateForm {
  return {
    name: brandTemplate.name,
    brandEanSpecification: brandTemplate.brandEanSpecification,
    brands: brandTemplate.brands,
    criteria: brandTemplate.criteria.map(({ criterion, values }) => ({
      criterion,
      values: values.map((value) => value.id),
    })),
  };
}

export function fromFormToQuery(
  id: string,
  data: IBrandTemplatePayload,
): ICriteriaUsageVariable {
  return {
    id: id,
    query: {
      criteria: data.criteria.map(({ criterion, values }) => ({
        criterion: criterion.id,
        values: values.filter((v) => v.id).map((v) => v.id),
      })),
    },
  };
}
